import { lazy, useEffect, useMemo, useState } from 'react';

import { Box, Center, Spinner, Text, VStack } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import { useUnit } from 'effector-react';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';

import { Button } from '@m/components-ui/button';
import { PAYMENT_OVERDUE_TODO } from '@m/constants/TodoTypes';
import { useCustomerFeatureFlag } from '@m/hooks/data/featureFlags';
import { useAutoAgreeToPendingAgreements } from '@m/hooks/data/terms';
import { useRequestCode, useUpdateUserInfo } from '@m/hooks/data/user';
import { addVehicle } from '@m/state/vehicle';
import { AddedVia, ProfileFieldSource } from '@m/types/grpc/MemberOnboardingSource';
import { useRequestVerifyCode } from 'apps/customer/containers/OnboardingFlow/hooks/useRequestVerifyCode';
import { useVerifyPhone } from 'apps/customer/containers/OnboardingFlow/hooks/useVerifyPhone';
import { useEnterpriseInvitationRedeem } from 'apps/customer/hooks/useEnterpriseInvitationRedeem';
import { useStripeClientSecret } from 'apps/customer/hooks/useStripeClientSecret';
import { useTrackViewedOnce } from 'apps/customer/hooks/useTrackViewedOnce';
import { LazyRoute } from 'apps/customer/routes/LazyRoute';
import {
  DefaultOnboardingRoutes,
  ProtectedRoutes,
  signInOnboardingPaths,
} from 'apps/customer/routes/paths';
import {
  membershipStore,
  setIndividualSubscriptionInvitationId,
} from 'apps/customer/state/membership';
import { fetchTodoItems } from 'apps/customer/state/todo';
import { getUser, userStore } from 'apps/customer/state/user';

import { deriveVariant } from '../deriveVariant';
import {
  trackOnboardingStepCompleted,
  trackOnboardingStepSkipped,
  trackOnboardingStepViewed,
} from '../onboardingHeap';

import ContainerHeader from './ContainerHeader';
import { onboardingSessionStore, setIsFlowComplete, setIsReturningUser } from './onboardingSession';
import { OnboardingStep } from './OnboardingStep';
import { resolveStepGuard } from './stepGuard';
import { isContactCollectedLast } from './stepOrder';
import { PATH_FOR_STEP, stepForPath } from './stepRoutes';
import { useOnboardingFlowResolution } from './useOnboardingFlowResolution';
import { useReviewSubscriptionData } from './useReviewSubscriptionData';
import { useSignInCompletionPath } from './useSignInCompletionPath';
import { useSignInOnboardingNavigation } from './useSignInOnboardingNavigation';

import type { FlowNextAction } from './flowResolution';
import type { TodoItem } from '@m/types/api';

const ContactInfoView = lazy(() => import('../views/ContactInfoView'));
const PaymentView = lazy(() => import('../views/PaymentView'));
const PhoneView = lazy(() => import('../views/PhoneView'));
const ReviewSubscriptionView = lazy(() => import('../views/ReviewSubscriptionView'));
const SuccessView = lazy(() => import('../views/SuccessView'));
const VehicleView = lazy(() => import('../views/VehicleView'));
const VerifyCodeView = lazy(() => import('../views/VerifyCodeView'));

function FlowLoading() {
  return (
    <Center height="100%">
      <Spinner />
    </Center>
  );
}

function FlowError({ message, retry }: { message: string; retry?: () => void }) {
  return (
    <Center height="100%" px="4">
      <VStack gap="4">
        <Text textAlign="center">{message}</Text>
        {retry && (
          <Button variant="secondary" onClick={retry}>
            Try again
          </Button>
        )}
      </VStack>
    </Center>
  );
}

export default function SignInOnboardingContainer() {
  return (
    <LazyRoute exact path={signInOnboardingPaths}>
      <SignInOnboardingBody />
    </LazyRoute>
  );
}

export function SignInOnboardingBody() {
  const location = useLocation();
  const rules = useOnboardingFlowResolution();
  const { finish, navigate } = useSignInOnboardingNavigation();
  const { user, phoneNumber, enterpriseUser } = useUnit(userStore);
  const currentStep = stepForPath(location.pathname);
  const [backOverride, setBackOverride] = useState<{ fn: () => void } | null>(null);
  const [isResolvingStep, setIsResolvingStep] = useState(false);
  const guardAction = useMemo(
    () =>
      currentStep && !isResolvingStep
        ? resolveStepGuard(currentStep, {
            hasCompletedFlow: rules.hasCompletedFlow,
            hasVerificationPhone: rules.hasVerificationPhone,
            hasVerifiedSession: rules.hasVerifiedSession,
            resumeAction: rules.resumeAction,
            variant: rules.variant,
          })
        : null,
    [
      currentStep,
      isResolvingStep,
      rules.hasCompletedFlow,
      rules.hasVerifiedSession,
      rules.hasVerificationPhone,
      rules.resumeAction,
      rules.variant,
    ],
  );

  useEffect(() => {
    if (guardAction) navigate(guardAction);
  }, [guardAction, navigate]);

  if (guardAction) return <FlowLoading />;

  return (
    <Box
      display="flex"
      flexDirection="column"
      width="100%"
      height="100dvh"
      bg="fill.background.default"
    >
      {currentStep && (
        <Box flexShrink="0">
          <ContainerHeader
            currentStep={currentStep}
            variant={rules.variant}
            backOverride={backOverride}
            onClose={() => {
              if (rules.variant === 'individualSub') {
                setIndividualSubscriptionInvitationId(null);
              }
              finish(ProtectedRoutes.HomePage);
            }}
          />
        </Box>
      )}
      <Box flex="1" minH="0" overflow="hidden">
        <Switch>
          <Route exact path={DefaultOnboardingRoutes.SignUp}>
            <Redirect to={PATH_FOR_STEP[OnboardingStep.AddEmail] + location.search} />
          </Route>
          <Route exact path={PATH_FOR_STEP[OnboardingStep.Landing]}>
            <LandingStep phoneNumber={phoneNumber} variant={rules.variant} onContinue={navigate} />
          </Route>
          <Route exact path={PATH_FOR_STEP[OnboardingStep.VerifyPhone]}>
            <VerifyPhoneStep phoneNumber={phoneNumber} rules={rules} onContinue={navigate} />
          </Route>
          <Route exact path={PATH_FOR_STEP[OnboardingStep.AddVehicle]}>
            <AddVehicleStep
              rules={rules}
              onContinue={navigate}
              setBackOverride={setBackOverride}
              setIsResolvingStep={setIsResolvingStep}
            />
          </Route>
          <Route exact path={PATH_FOR_STEP[OnboardingStep.AddPayment]}>
            <AddPaymentStep userId={user?.id} rules={rules} onContinue={navigate} />
          </Route>
          <Route exact path={PATH_FOR_STEP[OnboardingStep.AddEmail]}>
            <AddEmailStep
              phoneNumber={phoneNumber}
              user={user}
              enterpriseUser={enterpriseUser}
              rules={rules}
              onContinue={navigate}
            />
          </Route>
          <Route exact path={PATH_FOR_STEP[OnboardingStep.ReviewSubscription]}>
            <ReviewSubscriptionStep variant={rules.variant} onContinue={navigate} />
          </Route>
          <Route exact path={PATH_FOR_STEP[OnboardingStep.Success]}>
            <SuccessStep variant={rules.variant} />
          </Route>
        </Switch>
      </Box>
    </Box>
  );
}

function LandingStep({
  onContinue,
  phoneNumber,
  variant,
}: {
  onContinue: (action: FlowNextAction) => void;
  phoneNumber: string | null;
  variant: ReturnType<typeof deriveVariant>;
}) {
  const { mutateAsync: requestVerifyCode } = useRequestVerifyCode();
  const { data: hcaptchaEnabled = false } = useCustomerFeatureFlag('HcaptchaEnabled');
  useTrackViewedOnce(true, () => trackOnboardingStepViewed('Phone', variant));

  return (
    <PhoneView
      variant={variant}
      defaultValue={phoneNumber ?? ''}
      hcaptchaEnabled={hcaptchaEnabled}
      onContinue={async ({ hcaptchaToken, phoneNumber: entered }) => {
        setIsFlowComplete(false);
        await requestVerifyCode({ hcaptchaToken, phoneNumber: entered });
        trackOnboardingStepCompleted('Phone', variant);
        onContinue({ type: 'step', step: OnboardingStep.VerifyPhone });
      }}
    />
  );
}

function VerifyPhoneStep({
  onContinue,
  phoneNumber,
  rules,
}: {
  onContinue: (action: FlowNextAction) => void;
  phoneNumber: string | null;
  rules: ReturnType<typeof useOnboardingFlowResolution>;
}) {
  const { mutateAsync: verifyPhone } = useVerifyPhone();
  const { mutateAsync: resendCode } = useRequestCode();
  const autoAgreeToPendingAgreements = useAutoAgreeToPendingAgreements();
  useTrackViewedOnce(true, () => trackOnboardingStepViewed('VerifyCode', rules.variant));

  return (
    <VerifyCodeView
      phoneNumber={phoneNumber ?? undefined}
      onResend={
        phoneNumber
          ? async () => {
              await resendCode({ phoneNumber });
            }
          : undefined
      }
      onContinue={async ({ code }) => {
        const result = await verifyPhone({ otp: code });
        setIsReturningUser(!result.isNewAccount);
        setIsFlowComplete(false);
        try {
          await autoAgreeToPendingAgreements();
        } catch (error) {
          Sentry.captureException(error);
        }
        trackOnboardingStepCompleted('VerifyCode', rules.variant);
        onContinue(await rules.resolveAfterVerifyPhone(result));
      }}
    />
  );
}

function AddVehicleStep({
  onContinue,
  rules,
  setBackOverride,
  setIsResolvingStep,
}: {
  onContinue: (action: FlowNextAction) => void;
  rules: ReturnType<typeof useOnboardingFlowResolution>;
  setBackOverride: (override: { fn: () => void } | null) => void;
  setIsResolvingStep: (isResolving: boolean) => void;
}) {
  const [todoLoadError, setTodoLoadError] = useState<string | null>(null);
  useTrackViewedOnce(true, () => trackOnboardingStepViewed('Vehicle', rules.variant));

  useEffect(
    () => () => {
      setIsResolvingStep(false);
    },
    [setIsResolvingStep],
  );

  const continueWithTodos = (todoItems: TodoItem[]) => {
    const hasPaymentTask = todoItems.some((todoItem) => todoItem.type === PAYMENT_OVERDUE_TODO);
    onContinue(rules.resolveAfterAddVehicle(hasPaymentTask));
  };

  const retryTodoLoad = async () => {
    try {
      const response = await fetchTodoItems();
      const todoItems = response.data?.todoItems;
      if (!response.success || !todoItems) {
        setTodoLoadError(response.error || 'Unable to load your next onboarding step.');
        return;
      }

      continueWithTodos(todoItems);
    } catch (error) {
      setTodoLoadError(
        error instanceof Error ? error.message : 'Unable to load your next onboarding step.',
      );
    }
  };

  if (todoLoadError) {
    return (
      <FlowError
        message={`Your vehicle was saved, but we couldn't load your next step. ${todoLoadError}`}
        retry={() => {
          void retryTodoLoad();
        }}
      />
    );
  }

  return (
    <VehicleView
      setBackOverride={setBackOverride}
      onContinue={async (values) => {
        setIsResolvingStep(true);
        const response = await addVehicle({
          ...values,
          addedVia: AddedVia.EXPRESS_ONBOARDING,
        }).catch((error: unknown) => {
          setIsResolvingStep(false);
          throw error;
        });
        if (!response.success) {
          setIsResolvingStep(false);
          throw new Error(response.error || 'Add Vehicle Failed');
        }

        trackOnboardingStepCompleted('Vehicle', rules.variant);
        const todoItems = response.data?.todoItems;
        if (!todoItems) {
          setTodoLoadError('Please try loading the next step again.');
          return;
        }

        continueWithTodos(todoItems);
      }}
    />
  );
}

function AddPaymentStep({
  onContinue,
  rules,
  userId,
}: {
  onContinue: (action: FlowNextAction) => void;
  rules: ReturnType<typeof useOnboardingFlowResolution>;
  userId: number | undefined;
}) {
  const { data: clientSecret, isError, isFetching, refetch } = useStripeClientSecret(userId);
  useTrackViewedOnce(!!clientSecret, () => trackOnboardingStepViewed('Payment', rules.variant));

  if (userId == null) return <FlowError message="Your account could not be loaded." />;
  if (isError) {
    return (
      <FlowError
        message="Your payment form could not be loaded."
        retry={() => {
          void refetch();
        }}
      />
    );
  }
  if (!clientSecret) return <FlowLoading />;

  return (
    <PaymentView
      clientSecret={clientSecret}
      userId={userId}
      isFetching={isFetching}
      onSuccess={() => {
        trackOnboardingStepCompleted('Payment', rules.variant);
        onContinue(rules.resolveAfterAddPayment());
      }}
    />
  );
}

type ContactPrefillSource = {
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
};

function AddEmailStep({
  enterpriseUser,
  onContinue,
  phoneNumber,
  rules,
  user,
}: {
  enterpriseUser: ContactPrefillSource | undefined;
  onContinue: (action: FlowNextAction) => void;
  phoneNumber: string | null;
  rules: ReturnType<typeof useOnboardingFlowResolution>;
  user: ContactPrefillSource | null | undefined;
}) {
  const { mutateAsync: updateUserInfo } = useUpdateUserInfo();
  const isOptional = rules.contactInfoRequirement === 'none';
  const advance = () => onContinue(rules.resolveAfterAddEmail());
  useTrackViewedOnce(true, () =>
    trackOnboardingStepViewed('ContactInfo', rules.variant, isOptional),
  );

  return (
    <ContactInfoView
      variant={rules.variant}
      requirement={rules.contactInfoRequirement}
      collectsName
      defaultValues={{
        firstName: user?.firstName ?? enterpriseUser?.firstName ?? '',
        lastName: user?.lastName ?? enterpriseUser?.lastName ?? '',
        email: user?.email ?? enterpriseUser?.email ?? '',
      }}
      onContinue={async ({ firstName, lastName, email }) => {
        const hasAnyInput = !!email || !!firstName || !!lastName;
        if (isOptional && !hasAnyInput) {
          trackOnboardingStepSkipped('ContactInfo', rules.variant, isOptional);
          advance();
          return;
        }
        await updateUserInfo({
          firstName: firstName || undefined,
          lastName: lastName || undefined,
          email: email || undefined,
          phoneNumber: phoneNumber ?? undefined,
          profileFieldSource: ProfileFieldSource.EXPRESS_ONBOARDING,
        });
        await getUser();
        trackOnboardingStepCompleted('ContactInfo', rules.variant, isOptional);
        advance();
      }}
    />
  );
}

function ReviewSubscriptionStep({
  onContinue,
  variant,
}: {
  onContinue: (action: FlowNextAction) => void;
  variant: ReturnType<typeof deriveVariant>;
}) {
  const { individualSubscriptionInvitationId } = useUnit(membershipStore);
  const data = useReviewSubscriptionData(individualSubscriptionInvitationId);
  useTrackViewedOnce(data.status === 'ready', () =>
    trackOnboardingStepViewed('ReviewSubscription', variant),
  );

  if (data.status === 'loading') return <FlowLoading />;
  if (data.status === 'error') return <FlowError message={data.message} retry={data.retry} />;

  const { onContinue: purchase, ...viewProps } = data;
  return (
    <ReviewSubscriptionView
      {...viewProps}
      onContinue={async () => {
        await purchase();
        trackOnboardingStepCompleted('ReviewSubscription', variant);
        onContinue({ type: 'done' });
      }}
    />
  );
}

function SuccessStep({ variant }: { variant: ReturnType<typeof deriveVariant> }) {
  const { isReturningUser } = useUnit(onboardingSessionStore);
  const completionPath = useSignInCompletionPath();
  const { finish } = useSignInOnboardingNavigation();
  const isSubscription = variant === 'enterpriseSub' || variant === 'individualSub';
  const isNewUser = isReturningUser === false;
  const showsSuccessStep =
    isSubscription || isContactCollectedLast({ isNewAccount: isNewUser, variant });
  const { inviteRedemptionError, isRedeemingInvite, retryInviteRedemption } =
    useEnterpriseInvitationRedeem();
  const isSuccessVisible =
    showsSuccessStep &&
    !(variant === 'enterpriseSub' && (!!isRedeemingInvite || !!inviteRedemptionError));
  useTrackViewedOnce(isSuccessVisible, () => trackOnboardingStepViewed('Success', variant));

  useEffect(() => {
    if (!showsSuccessStep) finish(completionPath, { replace: true });
  }, [completionPath, finish, showsSuccessStep]);

  if (!showsSuccessStep) return <FlowLoading />;
  if (variant === 'enterpriseSub' && isRedeemingInvite) return <FlowLoading />;
  if (variant === 'enterpriseSub' && inviteRedemptionError) {
    return (
      <FlowError
        message="Your parking subscription could not be activated."
        retry={retryInviteRedemption}
      />
    );
  }

  return (
    <SuccessView
      isSubscription={isSubscription}
      isNewUser={isNewUser}
      onDone={() => {
        if (variant === 'individualSub') setIndividualSubscriptionInvitationId(null);
        finish(completionPath);
      }}
    />
  );
}
